import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertOctagon, ArrowDownRight, ArrowRight, ArrowUpRight, CheckCircle2, Clock, Headset, Loader, ShieldCheck } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { AnimatedNumber, Ring, Sparkline } from '@/components/viz/Kpi'
import { AreaChart, BarChart, Legend, type ChartSeries, type Day } from '@/components/viz/Charts'
import { artworkApi } from '@/features/artworks/api/artworkApi'
import { getActivityReport } from '@/features/activity/api/activityApi'
import type { DashboardReport, SalesWindow } from '../api/dashboardApi'

// public.sales is in the shop currency (GBP); the API converts it to USD once the analytics batch
// has recorded exchange rates, and says which in `sales.currency`.
const formatters = (currency: string) => {
  const symbol = currency === 'GBP' ? '£' : '$'
  return {
    money: (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n),
    moneyAxis: (n: number) => n >= 1000 ? `${symbol}${(n / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}k` : `${symbol}${n}`,
  }
}
const delay = (ms: number) => ({ animationDelay: `${ms}ms` }) as CSSProperties

export function Panel({ title, description, action, children, className, delayMs = 0 }: {
  title: string; description?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; delayMs?: number
}) {
  return <Card className={cn('viz-rise overflow-hidden', className)} style={delay(delayMs)}>
    <CardHeader className="pb-4"><div className="flex flex-wrap items-start justify-between gap-3">
      <div><CardTitle className="text-xl">{title}</CardTitle>{description && <CardDescription className="mt-1">{description}</CardDescription>}</div>{action}
    </div></CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
}

export function Unavailable({ what }: { what: string }) {
  return <p className="py-6 text-sm text-muted-foreground">{what} could not be read just now. The rest of the dashboard is unaffected; refresh to retry.</p>
}

/** Change against the previous window, as an arrow and a label (never color alone). */
function Delta({ now, before }: { now: number; before: number | undefined }) {
  if (before === undefined) return null
  if (!before) return now ? <span className="text-xs text-muted-foreground">new vs prior 30 days</span> : null
  const change = Math.round(100 * (now - before) / before)
  const Icon = change >= 0 ? ArrowUpRight : ArrowDownRight
  return <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
    <Icon className="h-3.5 w-3.5" aria-hidden /><span className="font-medium text-foreground">{change >= 0 ? '+' : ''}{change}%</span>&nbsp;vs prior 30 days
  </span>
}

type Severity = 'critical' | 'serious' | 'warning' | 'info'
const severityColor: Record<Severity, string> = {
  critical: 'var(--viz-critical)', serious: '#ec835a', warning: 'var(--viz-warning)', info: 'hsl(var(--muted-foreground))',
}

/** What needs a person, most urgent first. Each item links to the screen where it is handled. */
export function AttentionStrip({ report }: { report: DashboardReport }) {
  const items: { severity: Severity; icon: ReactNode; title: string; detail: string; to: string }[] = []
  const { publishing, support, approvals } = report
  if (publishing?.last24h.failed) {
    const cause = publishing.last24h.causes[0]
    items.push({ severity: 'critical', icon: <AlertOctagon className="h-5 w-5" />, to: '/app/tasks',
      title: `${publishing.last24h.failed} publish ${publishing.last24h.failed === 1 ? 'failure' : 'failures'} in 24 hours`,
      detail: cause ? `Most common: ${cause.pattern}` : 'See the task queue' })
  }
  const awaiting = support?.byStatus.awaiting_operator ?? 0
  if (awaiting) {
    items.push({ severity: support!.urgent ? 'serious' : 'warning', icon: <Headset className="h-5 w-5" />, to: '/app/support',
      title: `${awaiting} support ${awaiting === 1 ? 'ticket needs' : 'tickets need'} a reply`,
      detail: support!.urgent ? `${support!.urgent} open at high or urgent priority` : `${support!.open} open in total` })
  }
  if (approvals?.waiting) {
    items.push({ severity: 'warning', icon: <ShieldCheck className="h-5 w-5" />, to: '/app/export-artworks',
      title: `${approvals.waiting.toLocaleString()} ${approvals.waiting === 1 ? 'artwork' : 'artworks'} waiting for review`,
      detail: approvals.held ? `Plus ${approvals.held} on hold after a rights review` : 'Oldest submissions first' })
  }
  const queued = (publishing?.queue.pending ?? 0) + (publishing?.queue.running ?? 0)
  if (queued) {
    items.push({ severity: 'info', icon: <Loader className="h-5 w-5" />, to: '/app/tasks',
      title: `${queued.toLocaleString()} ${queued === 1 ? 'publish' : 'publishes'} in the queue`,
      detail: `${publishing!.queue.running} running now` })
  }
  if (!items.length) {
    return <div className="viz-rise flex items-center gap-3 rounded-xl border bg-card p-4 text-sm shadow-subtle">
      <CheckCircle2 className="h-5 w-5" style={{ color: 'var(--viz-good)' }} aria-hidden />
      <span><span className="font-medium">All clear.</span> <span className="text-muted-foreground">No failures, waiting tickets or review backlog right now.</span></span>
    </div>
  }
  return <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" role="list" aria-label="Needs attention">
    {items.map((item, i) => <Link key={item.title} to={item.to} role="listitem"
      className="viz-rise group relative flex gap-3 overflow-hidden rounded-xl border bg-card p-4 shadow-subtle transition-all hover:-translate-y-0.5 hover:shadow-card"
      style={delay(60 * i)}>
      <span aria-hidden className="absolute inset-y-0 left-0 w-1" style={{ background: severityColor[item.severity] }} />
      <span className="mt-0.5 shrink-0" style={{ color: severityColor[item.severity] }}>{item.icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium leading-snug">{item.title}</span>
        <span className="mt-1 block truncate text-xs text-muted-foreground" title={item.detail}>{item.detail}</span>
      </span>
      <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" aria-hidden />
    </Link>)}
  </div>
}

const salesMetrics = (currency: string): { key: keyof SalesWindow; label: string; format: (n: number) => string; axis: (n: number) => string }[] => {
  const { money, moneyAxis } = formatters(currency)
  return [
    { key: 'revenue', label: 'Revenue', format: money, axis: moneyAxis },
    { key: 'orders', label: 'Orders', format: n => n.toLocaleString(), axis: n => n.toLocaleString() },
    { key: 'units', label: 'Units', format: n => n.toLocaleString(), axis: n => n.toLocaleString() },
    { key: 'commission', label: 'Artist commission', format: money, axis: moneyAxis },
  ]
}

/** Sales over time: pick the measure and the window; the headline and chart follow. */
export function SalesHero({ sales }: { sales: NonNullable<DashboardReport['sales']> }) {
  const { money } = formatters(sales.currency)
  const metrics = salesMetrics(sales.currency)
  const [metricKey, setMetricKey] = useState<keyof SalesWindow>('revenue')
  const metric = metrics.find(m => m.key === metricKey)!
  const [range, setRange] = useState<30 | 90>(30)
  const data = sales.daily.slice(-range) as unknown as Day[]
  const total = sales.daily.slice(-range).reduce((n, d) => n + d[metric.key], 0)
  const current = sales.current ?? { orders: 0, units: 0, revenue: 0, commission: 0 }
  const previous = sales.previous ?? undefined
  const aov = current.orders ? current.revenue / current.orders : 0
  const series: ChartSeries = { key: metric.key, label: metric.label, color: 'var(--viz-sessions)' }
  return <Card className="viz-rise relative overflow-hidden">
    <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(circle at 0% 0%, color-mix(in oklab, var(--viz-sessions) 10%, transparent), transparent 45%)' }} />
    <CardContent className="relative grid gap-8 p-6 md:p-8 xl:grid-cols-[minmax(0,1fr)_17rem]">
      <div className="min-w-0">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-wider text-muted-foreground">{metric.label} · last {range} days</p>
            <p className="mt-2 font-serif text-5xl font-semibold leading-none"><AnimatedNumber value={total} format={metric.format} /></p>
            <div className="mt-2 min-h-5">{range === 30 && <Delta now={current[metric.key]} before={previous?.[metric.key]} />}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="inline-flex rounded-md border bg-card p-0.5 text-xs" role="group" aria-label="Measure">
              {metrics.map(m => <button key={m.key} type="button" aria-pressed={metric.key === m.key} onClick={() => setMetricKey(m.key)}
                className="rounded px-2.5 py-1 transition-colors aria-[pressed=true]:bg-foreground aria-[pressed=true]:text-background">{m.label}</button>)}
            </div>
            <div className="inline-flex rounded-md border bg-card p-0.5 text-xs" role="group" aria-label="Window">
              {([30, 90] as const).map(r => <button key={r} type="button" aria-pressed={range === r} onClick={() => setRange(r)}
                className="rounded px-2.5 py-1 transition-colors aria-[pressed=true]:bg-foreground aria-[pressed=true]:text-background">{r}d</button>)}
            </div>
          </div>
        </div>
        <div className="mt-6"><AreaChart data={data} series={series} format={metric.axis} height={300} label={`Daily ${metric.label.toLowerCase()}`}
          extra={d => metric.key !== 'orders' ? <p className="flex justify-between gap-4 text-muted-foreground"><span>Orders</span><span className="tabular-nums">{Number(d.orders)}</span></p> : null} /></div>
      </div>
      <dl className="grid grid-cols-2 gap-4 self-center xl:grid-cols-1">
        {[
          ['Orders', current.orders.toLocaleString(), <Delta key="o" now={current.orders} before={previous?.orders} />],
          ['Average order', money(aov), previous?.orders ? <Delta key="a" now={aov} before={previous.revenue / previous.orders} /> : null],
          ['Artist commission', money(current.commission), <span key="c" className="text-xs text-muted-foreground">{current.revenue ? Math.round(100 * current.commission / current.revenue) : 0}% of revenue</span>],
          ['Order lines, all time', sales.lines.toLocaleString(), <span key="l" className="text-xs text-muted-foreground">Fulfilled, as on Sales</span>],
        ].map(([label, value, note]) => <div key={label as string} className="rounded-lg border bg-background/60 p-3 backdrop-blur-sm">
          <dt className="text-xs text-muted-foreground">{label}</dt>
          <dd className="mt-0.5 font-serif text-2xl font-semibold tabular-nums">{value}</dd>
          <dd>{note}</dd>
        </div>)}
      </dl>
    </CardContent>
    <p className="relative px-6 pb-4 text-xs text-muted-foreground md:px-8">Fulfilled Shopify orders by the day they closed, Central time; revenue is line price × quantity in {sales.currency}. The headline counts the last {range} days; the side figures, the last 30.</p>
  </Card>
}

/** Artist uploads versus partner-catalog imports, per day. */
export function CatalogueGrowth({ catalogue }: { catalogue: NonNullable<DashboardReport['catalogue']> }) {
  const series: ChartSeries[] = [
    { key: 'uploads', label: 'Artist uploads', color: 'var(--viz-uploads)' },
    { key: 'imports', label: 'Pictufy imports', color: 'var(--viz-imports)' },
  ]
  const totals = catalogue.daily.reduce((t, d) => ({ uploads: t.uploads + d.uploads, imports: t.imports + d.imports }), { uploads: 0, imports: 0 })
  return <>
    <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
      <Legend series={series} />
      <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground tabular-nums">{totals.uploads.toLocaleString()}</span> uploaded · <span className="font-medium text-foreground tabular-nums">{totals.imports.toLocaleString()}</span> imported in 30 days</p>
    </div>
    <BarChart data={catalogue.daily as unknown as Day[]} series={series} label="Artworks added per day" />
    <p className="mt-3 text-xs text-muted-foreground">Uploads are distinct artworks artists uploaded themselves (observed since activity tracking began); imports are works the Pictufy pipeline brought in.</p>
  </>
}

/** Split bar: how much of a total comes from each part. */
export function SplitBar({ parts }: { parts: { label: string; value: number; color: string }[] }) {
  const total = parts.reduce((n, p) => n + p.value, 0) || 1
  return <div>
    <div className="flex h-2 gap-0.5 overflow-hidden rounded-full" style={{ background: 'var(--viz-empty)' }}>
      {parts.map(p => p.value > 0 && <div key={p.label} className="viz-grow-x h-full first:rounded-l-full last:rounded-r-full" style={{ width: `${100 * p.value / total}%`, background: p.color }} />)}
    </div>
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
      {parts.map(p => <span key={p.label} className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: p.color }} />{p.label} <span className="tabular-nums text-foreground">{p.value.toLocaleString()}</span></span>)}
    </div>
  </div>
}

export function TopSellers({ sales }: { sales: NonNullable<DashboardReport['sales']> }) {
  const { money } = formatters(sales.currency)
  const [view, setView] = useState<'artworks' | 'artists'>('artworks')
  const rows = view === 'artworks'
    ? sales.topArtworks.map(a => ({ id: a.id, name: a.name, sub: a.artist, revenue: a.revenue, units: a.units, to: `/app/artworks/${a.id}`, image: artworkApi.getArtworkImageUrl(a.id) }))
    : sales.topArtists.map(a => ({ id: a.id, name: a.name, sub: `${a.units} ${a.units === 1 ? 'unit' : 'units'}`, revenue: a.revenue, units: a.units, to: `/app/artists/${a.id}`, image: null }))
  const max = Math.max(1, ...rows.map(r => r.revenue))
  return <div>
    <div className="mb-3 inline-flex rounded-md border p-0.5 text-xs" role="group" aria-label="Rank">
      {(['artworks', 'artists'] as const).map(v => <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}
        className="rounded px-3 py-1 capitalize transition-colors aria-[pressed=true]:bg-foreground aria-[pressed=true]:text-background">{v}</button>)}
    </div>
    {!rows.length && <p className="py-4 text-sm text-muted-foreground">No fulfilled sales in the last 30 days.</p>}
    <ol className="space-y-1">
      {rows.map((r, i) => <li key={`${view}-${r.id}`} className="viz-rise flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-muted/60" style={delay(50 * i)}>
        <span className="w-4 text-center text-xs tabular-nums text-muted-foreground">{i + 1}</span>
        {r.image
          ? <img src={r.image} alt="" loading="lazy" className="h-10 w-10 shrink-0 rounded-md border object-cover" onError={e => { if (!e.currentTarget.src.includes('placeholder-image')) e.currentTarget.src = '/placeholder-image.svg' }} />
          : <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-muted text-[11px] font-medium">{r.name.split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase()}</span>}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <Link to={r.to} className="truncate text-sm font-medium hover:underline">{r.name}</Link>
            <span className="shrink-0 text-sm font-semibold tabular-nums">{money(r.revenue)}</span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full" style={{ background: 'var(--viz-empty)' }}>
            <div className="viz-grow-x h-full rounded-full" style={{ width: `${Math.max(2, 100 * r.revenue / max)}%`, background: 'var(--viz-sessions)', ...delay(50 * i + 150) }} />
          </div>
          <p className="mt-1 truncate text-xs text-muted-foreground">{r.sub}</p>
        </div>
      </li>)}
    </ol>
  </div>
}

export function PublishingHealth({ publishing }: { publishing: NonNullable<DashboardReport['publishing']> }) {
  const series: ChartSeries[] = [
    { key: 'completed', label: 'Published', color: 'var(--viz-active)' },
    { key: 'failed', label: 'Failed', color: 'var(--viz-critical)' },
  ]
  const { last24h, queue } = publishing
  return <div className="space-y-4">
    <div className="grid grid-cols-3 gap-3 text-center">
      {[['Published', last24h.completed, 'last 24h'], ['Failed', last24h.failed, last24h.recovered ? `${last24h.recovered} recovered on retry` : 'last 24h'], ['Queued', queue.pending + queue.running, `${queue.running} running`]]
        .map(([label, value, note]) => <div key={label as string} className="rounded-lg border p-2">
          <p className="font-serif text-2xl font-semibold tabular-nums">{Number(value).toLocaleString()}</p>
          <p className="text-xs">{label}</p><p className="truncate text-[11px] text-muted-foreground">{note}</p>
        </div>)}
    </div>
    <Legend series={series} />
    <BarChart data={publishing.daily as unknown as Day[]} series={series} height={160} label="Publish tasks per day, last 14 days" />
    {last24h.causes.length > 0 && <div>
      <p className="mb-2 flex items-center gap-1.5 text-xs font-medium"><AlertOctagon className="h-3.5 w-3.5" style={{ color: 'var(--viz-critical)' }} aria-hidden />Failure causes, last 24h</p>
      <ul className="space-y-1.5 text-xs">
        {last24h.causes.map(c => <li key={c.pattern} className="flex items-start justify-between gap-3 rounded-md bg-muted/50 px-2.5 py-1.5">
          <span className="min-w-0"><code className="break-words text-[11px]">{c.pattern}</code>
            {c.artworkIds.length > 0 && <span className="mt-0.5 block text-muted-foreground">Artworks {c.artworkIds.map((id, i) => <span key={id}>{i ? ', ' : ''}<Link className="hover:underline" to={`/app/artworks/${id}`}>{id}</Link></span>)}</span>}
          </span>
          <span className="shrink-0 font-medium tabular-nums">{c.count}×</span>
        </li>)}
      </ul>
    </div>}
  </div>
}

const ticketStatus: Record<string, string> = {
  new: 'New', agent_processing: 'Agent working', awaiting_customer: 'Waiting on customer',
  awaiting_operator: 'Needs a reply', operator_replied: 'Replied',
}

export function SupportLoad({ support }: { support: NonNullable<DashboardReport['support']> }) {
  const series: ChartSeries[] = [
    { key: 'created', label: 'Opened', color: 'var(--viz-sessions)' },
    { key: 'resolved', label: 'Resolved', color: 'var(--viz-active)' },
  ]
  const statuses = Object.entries(support.byStatus).sort((a, b) => b[1] - a[1])
  const max = Math.max(1, ...statuses.map(([, n]) => n))
  const response = support.medianFirstResponseMinutes
  return <div className="space-y-4">
    <div className="flex items-end justify-between gap-4">
      <div><p className="font-serif text-4xl font-semibold leading-none"><AnimatedNumber value={support.open} /></p><p className="mt-1 text-xs text-muted-foreground">open tickets</p></div>
      <div className="text-right text-xs text-muted-foreground"><Clock className="mb-0.5 mr-1 inline h-3.5 w-3.5" aria-hidden />
        Median first reply <span className="font-medium text-foreground">{response === null ? 'not measured' : response < 90 ? `${response} min` : `${Math.round(response / 60)} h`}</span><br />tickets from the last 30 days</div>
    </div>
    {statuses.length > 0 && <ul className="space-y-2">
      {statuses.map(([status, n], i) => <li key={status} className="grid grid-cols-[8.5rem_1fr_auto] items-center gap-3 text-xs">
        <span className="truncate">{ticketStatus[status] || status.replace(/_/g, ' ')}</span>
        <div className="h-2 overflow-hidden rounded-full" style={{ background: 'var(--viz-empty)' }}>
          <div className="viz-grow-x h-full rounded-full" style={{ width: `${100 * n / max}%`, background: status === 'awaiting_operator' ? 'var(--viz-warning)' : 'hsl(var(--muted-foreground))', ...delay(60 * i) }} />
        </div>
        <span className="w-6 text-right font-medium tabular-nums">{n}</span>
      </li>)}
    </ul>}
    <Legend series={series} />
    <BarChart data={support.daily as unknown as Day[]} series={series} mode="grouped" height={150} label="Support tickets opened and resolved per day, last 14 days" />
  </div>
}

/** Compact artist pulse: reach ring, active artists over 30 days, link through to the full report. */
export function ArtistPulse() {
  const query = useQuery({ queryKey: ['artist-activity', 30, '', 0, 'artists'], queryFn: () => getActivityReport(30, '', 0), refetchInterval: 60_000 })
  const report = query.data
  if (query.isError) return <Unavailable what="Artist activity" />
  if (!report) return <div className="h-40 animate-pulse rounded-lg bg-muted" />
  const { summary } = report
  return <div className="flex flex-wrap items-center gap-6">
    <Ring value={summary.activeInPeriod} total={summary.totalArtists} color="var(--viz-active)" size={120} stroke={11}>
      <span className="font-serif text-2xl font-semibold leading-none"><AnimatedNumber value={summary.totalArtists ? Math.round(100 * summary.activeInPeriod / summary.totalArtists) : 0} suffix="%" /></span>
      <span className="mt-0.5 text-[11px] text-muted-foreground">reach</span>
    </Ring>
    <div className="min-w-48 flex-1 space-y-3">
      <dl className="grid grid-cols-3 gap-3">
        {[['This week', summary.active7Days], ['30 days', summary.active30Days], ['Returning', summary.returningArtists]].map(([label, value]) => <div key={label as string}>
          <dd className="font-serif text-2xl font-semibold tabular-nums"><AnimatedNumber value={Number(value)} /></dd><dt className="text-xs text-muted-foreground">{label}</dt>
        </div>)}
      </dl>
      <Sparkline values={report.trend.map(d => d.active)} color="var(--viz-active)" label="Daily active artists, 30 days" />
      <p className="text-xs text-muted-foreground">{summary.uploads.toLocaleString()} artworks uploaded by {summary.uploaders.toLocaleString()} artists in 30 days · of {summary.totalArtists.toLocaleString()} artist accounts</p>
    </div>
  </div>
}

export function CurationQueue({ approvals }: { approvals: NonNullable<DashboardReport['approvals']> }) {
  const shown = useMemo(() => approvals.oldest, [approvals.oldest])
  if (!shown.length) return <p className="py-6 text-sm text-muted-foreground">No artworks are waiting for review.</p>
  return <div>
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
      {shown.map((a, i) => <Link key={a.id} to={`/app/artworks/${a.id}`} className="viz-rise group block" style={delay(40 * i)}>
        <div className="aspect-square overflow-hidden rounded-lg border bg-muted">
          <img src={artworkApi.getArtworkImageUrl(a.id)} alt={a.name} loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={e => { if (!e.currentTarget.src.includes('placeholder-image')) e.currentTarget.src = '/placeholder-image.svg' }} />
        </div>
        <p className="mt-1.5 truncate text-xs font-medium">{a.name}</p>
        <p className="truncate text-[11px] text-muted-foreground">{a.artist}</p>
      </Link>)}
    </div>
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
      <p className="text-xs text-muted-foreground">Oldest submissions first. {approvals.held > 0 && `${approvals.held} more are on hold after a rights review.`}</p>
      <Button asChild variant="outline" size="sm"><Link to="/app/export-artworks">Review all {approvals.waiting.toLocaleString()}<ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
    </div>
  </div>
}
